import { z } from "zod/v4";

import { createZodStringOption } from "components/Forms/utils/validation.utils";

import { CsfcDraftSchema, CsfcSchema } from "./SubSchemas/Csfc.schema";
import { EarDraftSchema, EarSchema } from "./SubSchemas/Ear.schema";
import { NistDraftSchema, NistSchema } from "./SubSchemas/Nist.schema";
import {
  ProtectionFilesDraftSchema,
  ProtectionProfilesSchema,
} from "./SubSchemas/ProtectionProfiles.schema";
import {
  SubmissionTypeDraftSchema,
  SubmissionTypeSchema,
} from "./SubSchemas/SubmissionType.schema";
import {
  TestedFeaturesDraftSchema,
  TestedFeaturesSchema,
} from "./SubSchemas/TestedFeatures.schema";

export interface FileMetadata {
  file_id?: number;
  file?: File;
  fileType?: string;
  documentVisibility?: string;
  fileLabel?: string;
  fileName?: string;
  active?: boolean;
}

export interface EarData {
  earid?: number;
  apriori: boolean;
  earFiles: FileMetadata[];
}

const ProductFileSchema = z.object({
  file: z.file().optional(),
  fileType: z.string(),
  documentVisibility: z.enum(["Proprietary", "Public"]),
  fileLabel: z.string(),
  fileName: z.string(),
  active: z.boolean(),
});

export const FinalFormSchema = z.object({
  formState: z.literal("final"),
  product_id: z.number().optional(),
  submissionType: SubmissionTypeSchema,
  product_description: z.string().min(1, "Product Description is Required"),
  product_name: z.string().min(1, "Product Name is Required"),
  tech_type: z
    .array(createZodStringOption())
    .min(1, "At least one Tech Type is Required"),
  version: z.string().min(1, "Product Version is Required"),
  protectionProfiles: ProtectionProfilesSchema,
  url: z.url("Please enter a valid URL"),
  publish_status: z.string().min(1, "Please select a Publish Status"),
  st_evaluation_date: z.string().min(1, "Please enter a ST Evaluation date"), // might want to look at date validations
  test_ready_date: z.string().min(1, "Please enter a Test Ready date"),
  testing_complete_date: z
    .string()
    .min(1, "Please enter a Testing Complete date"),
  evaluator_checkout_date: z
    .string()
    .min(1, "Please enter an Evaluator Checkout date"),
  files: z.array(ProductFileSchema).refine((files) => {
    return files.some((file) => file.fileType === "Security Target");
  }, "Security Target file is required"),
  //make sure vendor_id is not undefined
  vendor_id: z.number().min(1, "Required"),
  vendor_poc: z.string().min(1, "Required"),
  vendor_phone: z.string().min(1, "Required"),
  vendor_email: z.email().min(1, "Required"),
  csfc: CsfcSchema,
  nist: NistSchema,
  ears: EarSchema,
  testedFeatures: TestedFeaturesSchema,
  switchForm: z.boolean(),
  status: z.string(),
});

export const DraftCreateSchema = FinalFormSchema.pick({
  // required by the form
  product_name: true,
  vendor_id: true,

  // not required on drafts, but needed to build the payload
  switchForm: true,
  status: true,
}).extend({
  submissionType: SubmissionTypeDraftSchema,
  protectionProfiles: ProtectionFilesDraftSchema, // update to work with nested
  ears: EarDraftSchema,
  csfc: CsfcDraftSchema,
  nist: NistDraftSchema,
  testedFeatures: TestedFeaturesDraftSchema,
  formState: z.literal("draft-create"),
  tech_type: z.array(createZodStringOption()),
  publish_status: z.string(),
  files: z.array(ProductFileSchema),
});

export const DraftUpdateSchema = DraftCreateSchema.extend({
  formState: z.literal("draft-update"),
  product_id: z.number(),
});

export const ProductFormSchema = z.discriminatedUnion("formState", [
  FinalFormSchema,
  DraftCreateSchema,
  DraftUpdateSchema,
]);

export type ProductFormValues = z.infer<typeof ProductFormSchema>;

export type FinalFormTypes = z.infer<typeof FinalFormSchema>;
export type DraftUpdateTypes = z.infer<typeof DraftUpdateSchema>;
